import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import { CustomerFeatureFlagsContext } from "../../../../../contexts/CustomerFeatureFlagsContext";
import {
  FILE_EXTENSIONS,
  useFileNaming,
} from "../../../../../hooks/useFileNaming";
import {
  LATEST,
  OperationalHeatmapTypes,
  TimePoint,
} from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import CustomHeatMap from "../../../operational_heatmap/components/CustomHeatMap";
import HeatmapDialogPractice from "../../../operational_heatmap/HeatmapDIalogPractice";
import HeatmapLegend from "../../../operational_heatmap/HeatmapLegend";
import { getSeverityByCoverage } from "../../../operational_heatmap/utils";
import {
  baseTimePointOptions,
  createPdfHeader,
  createTrendSortMenuItems,
} from "../../utils/commonWidgetUtils";
import WidgetWrapper from "../../WidgetWrapper";

const HeatmapWidget = forwardRef(({ mainTimePoint }, ref) => {
  const colors = useColors();
  const { trackEvent, getHeatmapMatrixHistory } = useAPI();
  const { generatePdfFileName } = useFileNaming();
  const { heatmapDisplayMode } = useContext(CustomerFeatureFlagsContext);

  const [selectedCell, setSelectedCell] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMatrix, setSelectedMatrix] = useState({});
  const contentRef = useRef(null);
  const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);

  const {
    data: heatmapMatrixHistory,
    isLoading: isLoadingHeatmapMatrixHistory,
  } = useQuery({
    queryKey: ["heatmapMatrixHistory"],
    queryFn: getHeatmapMatrixHistory,
    select: (response) => response.data,
  });

  useEffect(() => {
    if (
      heatmapMatrixHistory &&
      heatmapMatrixHistory[heatmapDisplayMode] &&
      heatmapMatrixHistory[heatmapDisplayMode][mainTimePoint]
    ) {
      setBaseTimePoint(mainTimePoint);
    }
  }, [mainTimePoint, heatmapDisplayMode]);

  useEffect(() => {
    if (!heatmapMatrixHistory) return;

    const latestData = heatmapMatrixHistory[LATEST];
    if (!latestData) return;

    if (heatmapDisplayMode) {
      const displayModeData = latestData[heatmapDisplayMode];
      if (displayModeData) {
        setSelectedMatrix(displayModeData);
      }
    } else {
      setSelectedMatrix(latestData);
    }
  }, [heatmapMatrixHistory, heatmapDisplayMode]);

  const onCloseDialog = () => {
    setOpenDialog(false);
  };

  // TODO: add this once the new dashboard is fully implemented
  const mapMatrixToHeatmapData = (matrix, trendData = {}) => {
    if (!matrix) return [];
    return Object.entries(matrix)?.map(([domain, data]) => {
      return {
        id: domain,
        data: Object.entries(data)?.map(([entity, data]) => {
          // Get trend data for this specific domain/entity combination
          const entityTrend = trendData[domain]?.[entity];

          return {
            x: entity, // pillar
            y: {
              value:
                data?.controls?.length > 0
                  ? data.combined_severity
                    ? data.combined_severity
                    : getSeverityByCoverage(data.coverage)
                  : 0,
              coverage: data.coverage,
              // Add opacity to other cells if trend label is available
              opacityOtherCell: entityTrend?.trendLabel ? false : true,
              // Add custom label - show trend data if available, otherwise show coverage
              customLabel: entityTrend?.trendLabel,
              practice: Object.entries(data)
                .filter(
                  ([subCategory, data]) =>
                    typeof data === "object" && subCategory !== "controls"
                )
                ?.map(([subCategory, data]) => {
                  return {
                    subPillar: subCategory, // sub pillar
                    coverage: data.coverage,
                    severity:
                      data.controls.length > 0
                        ? data.combined_severity
                          ? data.combined_severity
                          : getSeverityByCoverage(data.coverage)
                        : 0,
                    practices: data.practices?.map((practice) => {
                      return {
                        subPillar: subCategory,
                        severity:
                          practice.controls.length > 0
                            ? practice.combined_severity
                              ? practice.combined_severity
                              : getSeverityByCoverage(practice.coverage)
                            : 0,
                        coverage: practice.coverage,
                        controls: practice.controls,
                        name: practice.name,
                        improvements: practice.improvements,
                      };
                    }),
                  };
                }),
            },
          };
        }),
      };
    });
  };

  // Calculate trend data for coverage changes
  const calculateCoverageTrends = useMemo(() => {
    if (
      !heatmapMatrixHistory ||
      !selectedMatrix ||
      baseTimePoint === TimePoint.LATEST
    ) {
      return {};
    }

    const currentMatrix = selectedMatrix;
    const baseTimepointData = heatmapMatrixHistory[baseTimePoint];
    const baseMatrix = heatmapDisplayMode
      ? baseTimepointData && baseTimepointData[heatmapDisplayMode]
      : baseTimepointData;

    if (!baseMatrix) return {};

    const trends = {};

    // Process each domain
    Object.keys(currentMatrix).forEach((domain) => {
      const currentDomain = currentMatrix[domain];
      const baseDomain = baseMatrix[domain];

      if (!currentDomain || !baseDomain) return;

      trends[domain] = {};

      // Process each entity within the domain
      Object.keys(currentDomain).forEach((entity) => {
        const currentEntity = currentDomain[entity];
        const baseEntity = baseDomain[entity];

        if (!currentEntity || !baseEntity) return;

        // Calculate coverage trend change
        const currentCoverage = currentEntity.coverage || 0;
        const baseCoverage = baseEntity.coverage || 0;
        const coverageChange = currentCoverage - baseCoverage;

        const trendLabel =
          coverageChange === 0
            ? ""
            : coverageChange > 0
            ? `+${coverageChange.toFixed(0)}%`
            : `${coverageChange.toFixed(0)}%`;

        trends[domain][entity] = {
          current: currentCoverage,
          previous: baseCoverage,
          change: coverageChange,
          isPositive: coverageChange > 0,
          trendLabel,
        };
      });
    });

    return trends;
  }, [heatmapMatrixHistory, selectedMatrix, baseTimePoint]);

  const data =
    selectedMatrix &&
    mapMatrixToHeatmapData(selectedMatrix, calculateCoverageTrends);

  const handleExportToPdf = useCallback(
    async (isCombinedExport = false) => {
      if (!contentRef.current || isLoadingHeatmapMatrixHistory) return;

      if (!isCombinedExport) {
        trackEvent(
          "User clicked on Export to PDF button in Controls Posture Heatmap"
        );
      }

      try {
        if (isCombinedExport) {
          // For combined export, use same html2canvas logic but return the canvas
          const container = contentRef.current;
          const currentDate = new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });

          // Create canvas with same settings as individual export
          const canvas = await html2canvas(container, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: colors.common_white,
            logging: false,
            imageTimeout: 0,
            onclone: (clonedDoc, element) => {
              // Create and add header with date
              const title = createPdfHeader("Controls Posture Heatmap");

              // Insert the header as the first child of the element
              element.insertBefore(title, element.firstChild);

              // Hide the export button in the cloned document
              const exportButton = element.querySelector(
                '[data-export-button="true"]'
              );
              if (exportButton instanceof HTMLElement) {
                exportButton.style.display = "none";
              }

              // Hide the popover in the cloned document
              const popover = element.querySelector(
                "#heatmap-score-calculation-popover"
              );
              if (popover instanceof HTMLElement) {
                popover.style.display = "none";
              }

              // Process all elements in the clone to ensure proper rendering
              const allElements = element.querySelectorAll("*");
              allElements.forEach((el) => {
                if (el instanceof HTMLElement) {
                  // Preserve original styles but ensure visibility
                  const computedStyle = window.getComputedStyle(el);
                  el.style.maxHeight = "none";
                  el.style.overflow = "visible";

                  // Preserve borders and shadows
                  if (
                    computedStyle.border !== "none" &&
                    computedStyle.border !== ""
                  ) {
                    el.style.border = computedStyle.border;
                  }
                  if (
                    computedStyle.borderRadius !== "none" &&
                    computedStyle.borderRadius !== ""
                  ) {
                    el.style.borderRadius = computedStyle.borderRadius;
                  }
                  if (
                    computedStyle.boxShadow !== "none" &&
                    computedStyle.boxShadow !== ""
                  ) {
                    el.style.boxShadow = computedStyle.boxShadow;
                  }
                }
              });
            },
          });

          // Create a container div and add the canvas as an image
          const widgetContainer = document.createElement("div");
          widgetContainer.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 100%;
          min-height: fit-content;
          page-break-inside: avoid;
        `;

          // Create image with proper loading handling
          return new Promise((resolve, reject) => {
            try {
              const img = document.createElement("img");
              const imageData = canvas.toDataURL("image/png", 1.0);

              img.onload = () => {
                resolve(widgetContainer);
              };

              img.onerror = (error) => {
                console.error("Heatmap image failed to load:", error);
                // Return container anyway to not break the entire export
                resolve(widgetContainer);
              };

              img.src = imageData;
              img.style.cssText = `
              width: 100%;
              height: auto;
              max-width: 100%;
              display: block;
            `;

              widgetContainer.appendChild(img);

              // Fallback timeout in case onload doesn't fire
              setTimeout(() => {
                resolve(widgetContainer);
              }, 1000);
            } catch (error) {
              console.error("Error creating heatmap image:", error);
              reject(error);
            }
          });
        } else {
          // Individual export - original functionality
          const container = contentRef.current;

          // Create canvas with high quality settings for heatmap
          const canvas = await html2canvas(container, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: colors.common_white,
            logging: false,
            imageTimeout: 0,
            onclone: (clonedDoc, element) => {
              // Create a wrapper div that will contain the header and the cloned content
              const wrapper = document.createElement("div");
              wrapper.style.overflow = "visible";
              wrapper.style.backgroundColor = "#ffffff";
              wrapper.style.padding = "20px";
              wrapper.style.fontFamily = "Arial, sans-serif";

              // Create and add header with date
              const title = createPdfHeader("Controls Posture Heatmap");
              // Insert the header as the first child of the element
              element.insertBefore(title, element.firstChild);

              // Hide the export button in the cloned document
              const exportButton = element.querySelector(
                '[data-export-button="true"]'
              );
              if (exportButton instanceof HTMLElement) {
                exportButton.style.display = "none";
              }

              // Hide the popover in the cloned document
              const popover = element.querySelector(
                "#heatmap-score-calculation-popover"
              );
              if (popover instanceof HTMLElement) {
                popover.style.display = "none";
              }

              // Process all elements in the clone to ensure proper rendering
              const allElements = element.querySelectorAll("*");
              allElements.forEach((el) => {
                if (el instanceof HTMLElement) {
                  // Preserve original styles but ensure visibility
                  const computedStyle = window.getComputedStyle(el);
                  el.style.maxHeight = "none";
                  el.style.overflow = "visible";

                  // Preserve borders and shadows
                  if (
                    computedStyle.border !== "none" &&
                    computedStyle.border !== ""
                  ) {
                    el.style.border = computedStyle.border;
                  }
                  if (
                    computedStyle.borderRadius !== "none" &&
                    computedStyle.borderRadius !== ""
                  ) {
                    el.style.borderRadius = computedStyle.borderRadius;
                  }
                  if (
                    computedStyle.boxShadow !== "none" &&
                    computedStyle.boxShadow !== ""
                  ) {
                    el.style.boxShadow = computedStyle.boxShadow;
                  }
                }
              });
            },
          });

          // Create PDF with compression
          const pdf = new jsPDF({
            orientation: "landscape",
            unit: "px",
            format: "a4",
            compress: true,
          });

          // Function to optimize image data
          const optimizeImageData = (canvas) => {
            return canvas.toDataURL("image/jpeg", 0.7);
          };

          // Add the canvas to the PDF with compression
          const imgData = optimizeImageData(canvas);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
          const imgX = (pdfWidth - imgWidth * ratio) / 2;
          const imgY = (pdfHeight - imgHeight * ratio) / 2;

          pdf.addImage(
            imgData,
            "JPEG",
            imgX,
            imgY,
            imgWidth * ratio,
            imgHeight * ratio,
            undefined,
            "FAST"
          );

          // Save the compressed PDF
          pdf.save(
            generatePdfFileName(
              "Controls_Posture_Heatmap",
              null,
              FILE_EXTENSIONS.PDF
            )
          );
        }
      } catch (error) {
        Toast.error(
          "Failed to export Controls Posture Heatmap, Please try again later"
        );
        trackEvent("Failed to export Controls Posture Heatmap", {
          error: error.message,
        });
        return null;
      }
    },
    [contentRef, isLoadingHeatmapMatrixHistory]
  );

  // Expose the export function to parent components
  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: handleExportToPdf,
      widgetId: "heatmap",
      hasExportCapability: true,
    }),
    [handleExportToPdf]
  );

  const sortMenuItems = createTrendSortMenuItems(
    baseTimePoint,
    setBaseTimePoint,
    heatmapMatrixHistory,
    icons,
    trackEvent,
    "controls-posture-heatmap",
    heatmapDisplayMode
  );

  const CalculationInfo = () => {
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Heatmap Score Calculation
        </Typography>
        {heatmapDisplayMode === OperationalHeatmapTypes.CONTROLS_BASED ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              How is the score calculated?
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Each gap contributes a score:
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, ml: 2 }}>
              • Low - 1 point
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, ml: 2 }}>
              • Medium - 3 points
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, ml: 2 }}>
              • High - 5 points
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              We accumulate the score points and categorize according to the
              threshold:
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, ml: 2 }}>
              • Very Low - 1 point
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, ml: 2 }}>
              • Low - 2 points
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, ml: 2 }}>
              • Medium - 3-4 points
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, ml: 2 }}>
              • High - 5-9 points
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, ml: 2 }}>
              • Very High - 10+ points
            </Typography>
            <Typography variant="subtitle2">
              To improve a tile's score, prioritize remediating its most severe
              gap.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              The score reflects several risk factors, including the number of
              affected entities, gap severity, and involvement in attack
              scenarios, among others.
            </Typography>
            <Typography variant="subtitle2">
              To improve a tile's score, prioritize remediating its most severe
              gap.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };
  return (
    <WidgetWrapper
      title={
        heatmapDisplayMode === OperationalHeatmapTypes.CONTROLS_BASED
          ? "Controls Heatmap"
          : "Risk Heatmap"
      }
      sortMenuItems={{
        data: sortMenuItems,
        tooltip: "View changes since selected time",
      }}
      exportToPdf={true}
      handleExportToPdf={() => handleExportToPdf(false)}
      calculationInfo={{
        label: "How is the score calculated?",
        content: <CalculationInfo />,
      }}
    >
      {isLoadingHeatmapMatrixHistory ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <LoadingIcon size={16} />
        </Box>
      ) : !selectedMatrix || Object.keys(selectedMatrix).length === 0 ? (
        <Box>
          <Typography variant="caption">No data available</Typography>
        </Box>
      ) : (
        <Box ref={contentRef} sx={{ padding: "0px 16px 0px 16px" }}>
          <Typography variant="caption" marginLeft={1}>
            {heatmapDisplayMode === OperationalHeatmapTypes.CONTROLS_BASED
              ? "Current posture"
              : "Current risk"}{" "}
            {baseTimePoint !== LATEST
              ? `vs. ${baseTimePointOptions[baseTimePoint]}`
              : ""}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              pt: "7px",
              gap: "19px",
              backgroundColor: colors.common_white,
              padding: "24px",
              borderRadius: "10px",
            }}
          >
            <Box
              sx={{
                borderRadius: "10px",
                display: "flex",
                gap: 2,
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                height: "535px",
                width: "100%",
              }}
            >
              <CustomHeatMap
                data={data}
                colors={colors}
                onCellClick={(cell) => {
                  setSelectedCell(cell);
                  setOpenDialog(true);
                }}
                showRisk={true}
                isLoading={isLoadingHeatmapMatrixHistory}
                customLabels={baseTimePoint !== TimePoint.LATEST ? true : false} // Enable custom labels
              />
              <HeatmapLegend showRisk={true} />
            </Box>
            <HeatmapDialogPractice
              open={openDialog}
              onClose={onCloseDialog}
              cell={selectedCell}
              showRisk={true}
              selectedTimePoint={LATEST}
            />
          </Box>
        </Box>
      )}
    </WidgetWrapper>
  );
});

HeatmapWidget.displayName = "HeatmapWidget";

export default HeatmapWidget;
